// ============================================================================
// FinanceDetailPage (E2, F-500/F-501) — DETAIL admin, /resultats-financiers/detail
// ============================================================================
// Portage du PoC app/(dashboard)/finance/page.tsx au standard maison. ADMIN GEST
// seul (M-500 requireAdmin, garde SERVEUR) : un non-admin qui force l'URL obtient un
// 403 -> ecran de refus i18n, jamais un ecran a moitie rendu.
//
// Segmented Visuels/Tableau deep-linkable ?view= (F-501) sur GsSegmented (primitive
// maison) ; le PoC rendait des <Link> — ici l'etat vit dans l'URL et TanStack Query
// garde les deux vues en cache (basculer ne recharge pas la tendance).
// M-507 : en vue « tableau » le serveur ne LIT PAS la tendance (trend: null).
//
// HORS SCOPE V1 (differes au frag E2b) : F-502 « Importer des donnees » et le lien
// saisie manuelle ne sont PAS rendus — aucun lien mort ; la lib import-* reste inerte
// jusqu'a E2b.

import { useSearchParams } from 'react-router-dom';
import { PageHeader, SkeletonCard, QueryError, GsSegmented } from '@gs/ui';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useFinanceDetail, type FinanceView } from '../../hooks/useFinance';
import { computeStatement } from '../../lib/finance/computations';
import { FinanceVisuals } from '../../components/finance/FinanceVisuals';
import { IncomeStatementTable } from '../../components/finance/IncomeStatementTable';
import { PeriodPicker } from '../../components/finance/PeriodPicker';
import { FinanceForbidden } from './ResultatsFinanciersPage';
import { formatMonthLong, formatStamp } from './format';

export default function FinanceDetailPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = i18n.language;

  const periodParam = searchParams.get('period');                       // F-507
  const view: FinanceView = searchParams.get('view') === 'tableau' ? 'tableau' : 'visuels'; // F-501

  const detailQ = useFinanceDetail(periodParam, view, !!user);
  const forbidden = detailQ.isError && detailQ.error instanceof ApiForbiddenError;
  const period = detailQ.data?.period ?? null;

  function setView(next: string) {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:finance.detail.title')}
        onRefresh={() => void detailQ.refetch()}
        refreshLoading={detailQ.isFetching}
        actions={period ? <PeriodPicker value={period} /> : undefined}
      />

      {forbidden ? (
        <FinanceForbidden />
      ) : (
        <>
          <GsSegmented
            ariaLabel={t('gest:finance.detail.viewLabel')}
            value={view}
            onChange={setView}
            options={[
              { value: 'visuels', label: t('gest:finance.detail.viewVisuels') },
              { value: 'tableau', label: t('gest:finance.detail.viewTableau') },
            ]}
          />

          {detailQ.isLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : detailQ.isError || !detailQ.data ? (
            <QueryError message={t('gest:finance.loadError')} onRetry={() => void detailQ.refetch()} />
          ) : (
            <>
              <p className="text-field text-slate-500 dark:text-slate-400">
                {t('gest:finance.summary.periodLabel', { month: formatMonthLong(detailQ.data.period, locale) })}
              </p>

              {view === 'visuels' ? (
                <FinanceVisuals
                  statement={computeStatement(detailQ.data.lines)}
                  summary={detailQ.data.summary}
                  trend={detailQ.data.trend}
                  locale={locale}
                  t={t}
                />
              ) : (
                <IncomeStatementTable
                  statement={computeStatement(detailQ.data.lines)}
                  locale={locale}
                  t={t}
                />
              )}

              <footer className="flex flex-wrap items-center justify-between gap-2 text-meta text-slate-400 dark:text-slate-500">
                <span>{t('gest:finance.detail.footer')}</span>
                <span>
                  {detailQ.data.summary.lastUpdatedAt
                    ? t('gest:finance.detail.lastUpdated', {
                        stamp: formatStamp(detailQ.data.summary.lastUpdatedAt, locale),
                      })
                    : t('gest:finance.detail.noData')}
                </span>
              </footer>
            </>
          )}
        </>
      )}
    </div>
  );
}
